import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

interface ReportIssueWidgetContextValue {
  isOpen: boolean
  openWidget: () => void
  closeWidget: () => void
}

const ReportIssueWidgetContext =
  createContext<ReportIssueWidgetContextValue | null>(null)

/**
 * Estado do painel de "Reportar Problema" — precisa ser compartilhado entre
 * `Sidebar` (que agora é o único gatilho, um item fixo no rodapé) e
 * `ReportIssueWidget` (que renderiza o drawer), duas árvores de componentes
 * diferentes montadas em pontos distintos de `App.tsx`.
 */
export function ReportIssueWidgetProvider({
  children,
}: {
  children: ReactNode
}) {
  const [isOpen, setIsOpen] = useState(false)
  const openWidget = useCallback(() => setIsOpen(true), [])
  const closeWidget = useCallback(() => setIsOpen(false), [])
  const value = useMemo(
    () => ({ isOpen, openWidget, closeWidget }),
    [isOpen, openWidget, closeWidget],
  )

  return (
    <ReportIssueWidgetContext.Provider value={value}>
      {children}
    </ReportIssueWidgetContext.Provider>
  )
}

export function useReportIssueWidget(): ReportIssueWidgetContextValue {
  const ctx = useContext(ReportIssueWidgetContext)
  if (!ctx) {
    throw new Error(
      'useReportIssueWidget deve ser usado dentro de ReportIssueWidgetProvider',
    )
  }
  return ctx
}
